import type {FC} from 'react'
import {useEffect, useMemo, useState} from 'react'
import {Box, CircularProgress, Skeleton, Tooltip, Typography} from '@mui/material'
import type {MessageAttachment} from '@interfaces/alpinist/message'
import {
  useLazyPreviewMessageAttachmentQuery,
  usePreviewMessageAttachmentQuery,
} from '@apis/aiCoreAPI.ts'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import {attachmentKind, isImageAttachment} from '@containers/Dashboards/AgentChat/attachments'
import ImageThumbnail from '@containers/Dashboards/AgentChat/components/messages/ImageThumbnail'
import AttachmentIcon from '@containers/Dashboards/AgentChat/components/messages/AttachmentIcon'
import {saveBlob} from '@shared/utils/saveBlob'
import AttachmentKeepButton from '@containers/Dashboards/AgentChat/components/messages/AttachmentKeepButton'
import {useReadingProgress} from '@containers/Dashboards/AgentChat/components/chatMessageList/IndexingContext'
import DocumentPreviewModal from '@shared/components/DocumentPreviewModal'

/**
 * One attached image of a sent user message. Bytes come through the
 * authenticated preview endpoint (storage URLs are short-lived SAS links, so
 * the backend never hands them out); the blob is turned into an object URL for
 * the <img>. Click opens the full-size image.
 */
const AttachmentImage: FC<{conversationId: string; attachment: MessageAttachment}> = ({
  conversationId,
  attachment,
}) => {
  const {data: blob} = usePreviewMessageAttachmentQuery({
    conversation_id: conversationId,
    attachment_id: attachment.id,
  })

  const objectUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob])
  useEffect(
    () => () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    },
    [objectUrl],
  )

  if (!objectUrl) {
    return <Skeleton variant="rounded" width={120} height={90} />
  }

  return <ImageThumbnail src={objectUrl} alt={attachment.original_filename} />
}

/**
 * A non-image attachment — a spreadsheet, PDF, Word document or slide deck — of
 * a sent user message.
 *
 * Clicking it opens the same modal a library row or a citation opens, so a file
 * dropped into chat can be inspected where it was dropped: its schema if it is
 * queried, the passage Alpinist read otherwise, and the file itself either way.
 * It used to download on click, which was the only thing a chat attachment
 * could do — the reader had to open it in another application to see what
 * Alpinist was working from.
 *
 * The download stays the behaviour for an attachment with no document behind it
 * (one still being ingested, or one that never became a document): there is
 * nothing to open yet, and the bytes are only fetched when asked for.
 */
const AttachmentFile: FC<{
  conversationId: string
  attachment: MessageAttachment
  onOpen?: () => void
}> = ({conversationId, attachment, onOpen}) => {
  const [t] = useCustomTranslation('alpinist')
  const [fetchFile, {isFetching}] = useLazyPreviewMessageAttachmentQuery()
  // The turn is waiting for this file to finish being indexed (AB#5992). It
  // belongs on the card rather than in a line of its own below the bubble: the
  // state is about *this* file, and with two attachments a floating "Reading…"
  // cannot say which one it means.
  const {reading, percent} = useReadingProgress(attachment.document?.document_id)

  const download = async (): Promise<void> => {
    const blob = await fetchFile({
      conversation_id: conversationId,
      attachment_id: attachment.id,
    }).unwrap()
    saveBlob(blob, attachment.original_filename || 'attachment')
  }

  // Not while it is still being ingested: outline and read have nothing to
  // answer with yet, so the modal would open onto its own error.
  const openable = Boolean(onOpen) && Boolean(attachment.document?.document_id) && !reading

  return (
    <Tooltip title={openable ? t('attachment_open') : t('attachment_download')}>
      <Box
        onClick={isFetching ? undefined : openable ? onOpen : download}
        display="flex"
        alignItems="center"
        gap={0.75}
        px={1}
        py={0.75}
        maxWidth={220}
        sx={{
          borderRadius: 1.5,
          border: 1,
          borderColor: 'divider',
          cursor: isFetching ? 'progress' : 'pointer',
          bgcolor: 'background.paper',
        }}
      >
        <AttachmentIcon kind={attachmentKind(attachment.mime_type)} />
        <Box sx={{minWidth: 0}}>
          <Typography variant="caption" noWrap display="block">
            {attachment.original_filename}
          </Typography>
          {reading && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              {/* Determinate as soon as ingest knows how much work the file is;
                  a spinner alone, for minutes, reads as stuck. */}
              <CircularProgress
                size={10}
                thickness={6}
                variant={percent === null ? 'indeterminate' : 'determinate'}
                value={percent ?? undefined}
              />
              <Typography variant="caption" noWrap color="text.secondary">
                {/* "Preparing" until ingest knows how much work the file is —
                    the parse has to finish before there is a total to count
                    against, and a percentage before then would be invented. */}
                {percent === null
                  ? t('attachment_preparing')
                  : t('attachment_indexing_percent', {percent})}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Tooltip>
  )
}

/**
 * The files of a message: a user's, right-aligned above its bubble, or
 * Alpinist's (the screen it looked at, AB#6762), left-aligned in its answer.
 */
const UserAttachments: FC<{
  conversationId: string
  attachments: MessageAttachment[]
  align?: 'start' | 'end'
}> = ({conversationId, attachments, align = 'end'}) => {
  // One modal for the group rather than one per chip: only one can be open, and
  // a chip that unmounts mid-fetch (a re-render while the turn streams) would
  // take its own modal with it.
  const [opened, setOpened] = useState<MessageAttachment | null>(null)

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.75,
        flexWrap: 'wrap',
        justifyContent: align === 'end' ? 'flex-end' : 'flex-start',
      }}
    >
      {attachments.map((attachment) => (
        <Box key={attachment.id} sx={{display: 'flex', alignItems: 'center', gap: 0.25}}>
          {isImageAttachment(attachment) ? (
            <AttachmentImage conversationId={conversationId} attachment={attachment} />
          ) : (
            <AttachmentFile
              conversationId={conversationId}
              attachment={attachment}
              onOpen={() => setOpened(attachment)}
            />
          )}
          {/* A file dropped into a chat is searchable here and nowhere else.
            That is the right default, but without a way out it is a dead end —
            the only remedy was uploading it a second time from Settings
            (AB#5992). Shown whatever scope the file is in, because saving it
            has to be undoable: gated on 'conversation', the control vanished
            the moment it worked. */}
          {attachment.document?.can_change_visibility &&
            attachment.document.visibility &&
            attachment.document.document_id && (
              <AttachmentKeepButton
                documentId={attachment.document.document_id}
                documentName={attachment.original_filename}
                visibility={attachment.document.visibility}
              />
            )}
        </Box>
      ))}
      <DocumentPreviewModal
        open={Boolean(opened)}
        onClose={() => setOpened(null)}
        documentId={opened?.document?.document_id ?? null}
        title={opened?.original_filename}
        mimeType={opened?.mime_type}
        filename={opened?.original_filename}
      />
    </Box>
  )
}

export default UserAttachments
